import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  CardActions,
  Grid,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Snackbar,
  Alert,
  Tooltip,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Paper,
  CircularProgress,
  Tabs,
  Tab,
  Fab
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Business as BusinessIcon,
  LocationOn as LocationIcon,
  ArrowBack as ArrowBackIcon,
  Schedule as ScheduleIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Build as BuildIcon,
  Close as CloseIcon,
  DragHandle as DragHandleIcon,
  DragIndicator,
  Save as SaveIcon,
  Inventory as InventoryIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import FullScreenLayout from './layouts/FullScreenLayout';
import ProductionPlantInventory from './ProductionPlantInventory';
import { productionPlantsApi } from '../services/productionPlantsApi';

// Styled components for modern black and white design
const HeaderContainer = styled(Box)(({ theme }) => ({
  backgroundColor: '#ffffff',
  borderBottom: '1px solid #e0e0e0',
  padding: theme.spacing(3),
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
}));



const SectionCard = styled(Card)(({ theme }) => ({
  backgroundColor: '#ffffff',
  border: '1px solid #e0e0e0',
  borderRadius: '8px',
  marginBottom: theme.spacing(2),
  transition: 'all 0.3s ease',
  '&:hover': {
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
    transform: 'translateY(-2px)',
  },
}));

const DraggableSectionCard = styled(Card)(({ theme, isDragging }) => ({
  backgroundColor: '#ffffff',
  border: '1px solid #e0e0e0',
  borderRadius: '8px',
  marginBottom: theme.spacing(2),
  transition: 'all 0.3s ease',
  transform: isDragging ? 'rotate(5deg)' : 'none',
  boxShadow: isDragging ? '0 8px 24px rgba(0, 0, 0, 0.2)' : '0 2px 8px rgba(0, 0, 0, 0.08)',
  '&:hover': {
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
    transform: isDragging ? 'rotate(5deg)' : 'translateY(-2px)',
  },
}));

const StyledChip = styled(Chip)(({ theme, status }) => ({
  fontWeight: 600,
  fontSize: '0.75rem',
  height: '24px',
  backgroundColor: 
    status === 'active' ? '#000000' :
    status === 'inactive' ? '#757575' :
    status === 'maintenance' ? '#ff9800' :
    '#f44336',
  color: '#ffffff',
  '& .MuiChip-label': {
    padding: '0 8px',
  },
}));

const TabContainer = styled(Box)(({ theme }) => ({
  backgroundColor: '#ffffff',
  borderRadius: '12px',
  border: '1px solid #e0e0e0',
  overflow: 'hidden',
}));

const EmptyState = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: theme.spacing(6),
  textAlign: 'center',
  backgroundColor: '#f8f9fa',
  borderRadius: '8px',
  border: '2px dashed #e0e0e0',
}));

const ProductionPlantDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  
  // State management
  const [plant, setPlant] = useState(null);
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sectionsLoading, setSectionsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('sections');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingSection, setEditingSection] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [sectionToDelete, setSectionToDelete] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [reorderMode, setReorderMode] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // Form state for section dialog
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    section_order: '',
    status: 'active',
    estimated_processing_time_hours: ''
  });

  const statusOptions = [
    { value: 'active', label: 'Active', icon: <CheckCircleIcon /> },
    { value: 'inactive', label: 'Inactive', icon: <CancelIcon /> },
    { value: 'maintenance', label: 'Maintenance', icon: <BuildIcon /> },
    { value: 'closed', label: 'Closed', icon: <CloseIcon /> }
  ];

  useEffect(() => {
    if (id) {
      fetchPlantDetails();
      fetchSections();
    }
  }, [id]);

  const fetchPlantDetails = async () => {
    try {
      setLoading(true);
      const response = await productionPlantsApi.getPlant(id);
      setPlant(response.data);
    } catch (error) {
      console.error('Error fetching plant details:', error);
      showSnackbar('Failed to fetch plant details', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchSections = async () => {
    try {
      setSectionsLoading(true);
      const response = await productionPlantsApi.getPlantSections(id);
      setSections(response.data || []);
    } catch (error) {
      console.error('Error fetching sections:', error);
      showSnackbar('Failed to fetch sections', 'error');
    } finally {
      setSectionsLoading(false);
    }
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleOpenDialog = (section = null) => {
    if (section) {
      setEditingSection(section);
      setFormData({
        name: section.name || '',
        description: section.description || '',
        section_order: section.section_order || '',
        status: section.status || 'active',
        estimated_processing_time_hours: section.estimated_processing_time_hours || ''
      });
    } else {
      setEditingSection(null);
      const nextOrder = sections.length > 0 ? Math.max(...sections.map(s => s.section_order)) + 1 : 1;
      setFormData({
        name: '',
        description: '',
        section_order: nextOrder,
        status: 'active',
        estimated_processing_time_hours: ''
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingSection(null);
    setFormData({
      name: '',
      description: '',
      section_order: '',
      status: 'active',
      estimated_processing_time_hours: ''
    });
  };

  const handleFormChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async () => {
    try {
      if (!formData.name.trim()) {
        showSnackbar('Section name is required', 'error');
        return;
      }

      const submitData = {
        ...formData,
        section_order: parseInt(formData.section_order) || 1,
        estimated_processing_time_hours: formData.estimated_processing_time_hours ? 
          parseFloat(formData.estimated_processing_time_hours) : null
      };

      if (editingSection) {
        await productionPlantsApi.updateSection(editingSection.section_id, submitData);
        showSnackbar('Section updated successfully');
      } else {
        await productionPlantsApi.createSection(id, submitData);
        showSnackbar('Section created successfully');
      }

      handleCloseDialog();
      fetchSections();
    } catch (error) {
      console.error('Error saving section:', error);
      showSnackbar(
        error.response?.data?.message || 'Failed to save section',
        'error'
      );
    }
  };

  const handleDeleteClick = (section) => {
    setSectionToDelete(section);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      await productionPlantsApi.deleteSection(sectionToDelete.section_id);
      showSnackbar('Section deleted successfully');
      setDeleteDialogOpen(false);
      setSectionToDelete(null);
      fetchSections();
    } catch (error) {
      console.error('Error deleting section:', error);
      showSnackbar(
        error.response?.data?.message || 'Failed to delete section',
        'error'
      );
    }
  };

  const handleDragEnd = (result) => {
    if (!result.destination) return;

    const items = Array.from(sections);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    // Update section orders
    const updatedSections = items.map((section, index) => ({
      ...section,
      section_order: index + 1
    }));

    setSections(updatedSections);
    setHasUnsavedChanges(true);
  };

  const handleSaveOrder = async () => {
    try {
      const sectionsToUpdate = sections.map(section => ({
        section_id: section.section_id,
        section_order: section.section_order
      }));

      await productionPlantsApi.reorderSections(id, sectionsToUpdate);
      showSnackbar('Section order saved successfully');
      setHasUnsavedChanges(false);
      setReorderMode(false);
    } catch (error) {
      console.error('Error saving section order:', error);
      showSnackbar('Failed to save section order', 'error');
      // Revert changes
      fetchSections();
      setHasUnsavedChanges(false);
    }
  };

  const handleCancelReorder = () => {
    setReorderMode(false);
    setHasUnsavedChanges(false);
    fetchSections(); // Revert to original order
  };

  const getStatusIcon = (status) => {
    const statusOption = statusOptions.find(opt => opt.value === status);
    return statusOption ? statusOption.icon : <CheckCircleIcon />;
  };

  const renderSectionCard = (section, index) => {
    if (reorderMode) {
      return (
        <Draggable key={section.section_id} draggableId={section.section_id.toString()} index={index}>
          {(provided, snapshot) => (
            <div
              ref={provided.innerRef}
              {...provided.draggableProps}
              style={provided.draggableProps.style}
            >
              <DraggableSectionCard isDragging={snapshot.isDragging}>
                <CardContent>
                  <Box display="flex" alignItems="center" gap={2}>
                    <div {...provided.dragHandleProps}>
                      <DragHandleIcon sx={{ color: '#757575', cursor: 'grab' }} />
                    </div>
                    <Box flex={1}>
                      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                        <Typography variant="h6" fontWeight={600}>
                          {section.section_order}. {section.name}
                        </Typography>
                        <StyledChip
                          label={section.status}
                          status={section.status}
                          icon={getStatusIcon(section.status)}
                          size="small"
                        />
                      </Box>
                      {section.description && (
                        <Typography variant="body2" color="text.secondary" mb={1}>
                          {section.description}
                        </Typography>
                      )}
                      {section.estimated_processing_time_hours && (
                        <Box display="flex" alignItems="center" gap={0.5}>
                          <ScheduleIcon sx={{ fontSize: '1rem', color: '#757575' }} />
                          <Typography variant="body2" color="text.secondary">
                            {section.estimated_processing_time_hours} hours
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </Box>
                </CardContent>
              </DraggableSectionCard>
            </div>
          )}
        </Draggable>
      );
    }

    return (
      <SectionCard key={section.section_id}>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
            <Box flex={1}>
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <Typography variant="h6" fontWeight={600} color="#000000">
                  {section.section_order}. {section.name}
                </Typography>
                <StyledChip
                  label={section.status}
                  status={section.status}
                  icon={getStatusIcon(section.status)}
                  size="small"
                />
              </Box>
              {section.description && (
                <Typography variant="body2" color="text.secondary" mb={1}>
                  {section.description}
                </Typography>
              )}
              {section.estimated_processing_time_hours && (
                <Box display="flex" alignItems="center" gap={0.5}>
                  <ScheduleIcon sx={{ fontSize: '1rem', color: '#757575' }} />
                  <Typography variant="body2" color="text.secondary">
                    Estimated processing time: {section.estimated_processing_time_hours} hours
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
        </CardContent>
        <CardActions sx={{ justifyContent: 'flex-end', px: 2, pb: 2 }}>
          <Tooltip title="Edit Section">
            <IconButton
              size="small"
              onClick={() => handleOpenDialog(section)}
              sx={{ color: '#000000' }}
            >
              <EditIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete Section">
            <IconButton
              size="small"
              onClick={() => handleDeleteClick(section)}
              sx={{ color: '#f44336' }}
            >
              <DeleteIcon />
            </IconButton>
          </Tooltip>
        </CardActions>
      </SectionCard>
    );
  };

  const renderSectionsTab = () => (
    <Box sx={{ p: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h5" fontWeight={600} color="#000000">
          Production Sections
        </Typography>
        <Box display="flex" gap={2}>
          {sections.length > 1 && !reorderMode && (
            <Button
              variant="outlined"
              startIcon={<DragIndicator />}
              onClick={() => setReorderMode(true)}
              sx={{
                borderColor: '#000000',
                color: '#000000',
                '&:hover': {
                  backgroundColor: '#f5f5f5',
                  borderColor: '#000000',
                },
              }}
            >
              Reorder Sections
            </Button>
          )}
          {reorderMode && (
            <>
              <Button
                variant="outlined"
                onClick={handleCancelReorder}
                sx={{ color: '#757575', borderColor: '#757575' }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                startIcon={<SaveIcon />}
                onClick={handleSaveOrder}
                disabled={!hasUnsavedChanges}
                sx={{
                  backgroundColor: '#000000',
                  color: '#ffffff',
                  '&:hover': { backgroundColor: '#333333' },
                }}
              >
                Save Order
              </Button>
            </>
          )}
          {!reorderMode && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog()}
              sx={{
                backgroundColor: '#000000',
                color: '#ffffff',
                fontWeight: 600,
                '&:hover': { backgroundColor: '#333333' },
              }}
            >
              Add Section
            </Button>
          )}
        </Box>
      </Box>

      {sectionsLoading ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
          <CircularProgress sx={{ color: '#000000' }} />
        </Box>
      ) : sections.length === 0 ? (
        <EmptyState>
          <BusinessIcon sx={{ fontSize: '4rem', color: '#e0e0e0', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No Sections Found
          </Typography>
          <Typography variant="body2" color="text.secondary" mb={3}>
            Create your first production section to get started
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
            sx={{
              backgroundColor: '#000000',
              color: '#ffffff',
              '&:hover': { backgroundColor: '#333333' },
            }}
          >
            Add First Section
          </Button>
        </EmptyState>
      ) : reorderMode ? (
        <DragDropContext onDragEnd={handleDragEnd}>
          <Droppable droppableId="sections">
            {(provided) => (
              <div {...provided.droppableProps} ref={provided.innerRef}>
                {sections
                  .sort((a, b) => a.section_order - b.section_order)
                  .map((section, index) => renderSectionCard(section, index))}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      ) : (
        <Box>
          {sections
            .sort((a, b) => a.section_order - b.section_order)
            .map((section) => renderSectionCard(section))}
        </Box>
      )}
    </Box>
  );

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress sx={{ color: '#000000' }} />
      </Box>
    );
  }

  if (!plant) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <Typography variant="h6" color="text.secondary">
          Plant not found
        </Typography>
      </Box>
    );
  }

  const header = (
    <HeaderContainer>
      <Box display="flex" alignItems="center" gap={2}>
        <IconButton
          onClick={() => navigate('/production-plants')}
          sx={{ color: '#000000' }}
        >
          <ArrowBackIcon />
        </IconButton>
        <Box flex={1}>
          <Box display="flex" alignItems="center" gap={2} mb={0.5}>
            <Typography variant="h4" fontWeight={700} color="#000000">
              {plant.name}
            </Typography>
            <StyledChip
              label={plant.status}
              status={plant.status}
              icon={getStatusIcon(plant.status)}
              size="small"
            />
          </Box>
          <Box display="flex" alignItems="center" gap={3}>
            <Typography variant="body2" color="text.secondary">
              Created: {new Date(plant.created_at).toLocaleDateString()}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Sections: {sections.length}
            </Typography>
            {plant.location && (
              <Box display="flex" alignItems="center" gap={0.5}>
                <LocationIcon sx={{ fontSize: '1rem', color: '#757575' }} />
                <Typography variant="body2" color="text.secondary">
                  {plant.location}
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    </HeaderContainer>
  );

  const mainContent = (
    <Box sx={{ p: 3 }}>
      {/* Plant Description */}
      {plant.description && (
        <Box sx={{ mb: 3, p: 2, backgroundColor: '#f8f9fa', borderRadius: '8px', border: '1px solid #e0e0e0' }}>
          <Typography variant="body1" color="text.secondary">
            {plant.description}
          </Typography>
        </Box>
      )}

      {/* Tabs */}
      <TabContainer>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          sx={{
            borderBottom: 1,
            borderColor: 'divider',
            '& .MuiTab-root': {
              textTransform: 'none',
              fontWeight: 500,
              fontSize: '1rem',
              color: '#757575',
              '&.Mui-selected': {
                color: '#000000',
                fontWeight: 600,
              },
            },
            '& .MuiTabs-indicator': {
              backgroundColor: '#000000',
            },
          }}
        >
          <Tab label="Sections" value="sections" />
          <Tab label="Inventory" value="inventory" />
        </Tabs>

        {/* Tab Content */}
        {activeTab === 'sections' && renderSectionsTab()}
        {activeTab === 'inventory' && <ProductionPlantInventory />}
      </TabContainer>
    </Box>
  );

  return (
    <>
      <FullScreenLayout
        header={header}
        mainPanel={mainContent}
      />

      {/* Add/Edit Section Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '12px',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
          },
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Typography variant="h6" fontWeight={600}>
            {editingSection ? 'Edit Section' : 'Add New Section'}
          </Typography>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 3 }}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Section Name"
                value={formData.name}
                onChange={(e) => handleFormChange('name', e.target.value)}
                required
                variant="outlined"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '&.Mui-focused fieldset': {
                      borderColor: '#000000',
                    },
                  },
                  '& .MuiInputLabel-root.Mui-focused': {
                    color: '#000000',
                  },
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                value={formData.description}
                onChange={(e) => handleFormChange('description', e.target.value)}
                multiline
                rows={3}
                variant="outlined"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '&.Mui-focused fieldset': {
                      borderColor: '#000000',
                    },
                  },
                  '& .MuiInputLabel-root.Mui-focused': {
                    color: '#000000',
                  },
                }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Section Order"
                type="number"
                value={formData.section_order}
                onChange={(e) => handleFormChange('section_order', e.target.value)}
                variant="outlined"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '&.Mui-focused fieldset': {
                      borderColor: '#000000',
                    },
                  },
                  '& .MuiInputLabel-root.Mui-focused': {
                    color: '#000000',
                  },
                }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Processing Time (hours)"
                type="number"
                value={formData.estimated_processing_time_hours}
                onChange={(e) => handleFormChange('estimated_processing_time_hours', e.target.value)}
                variant="outlined"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '&.Mui-focused fieldset': {
                      borderColor: '#000000',
                    },
                  },
                  '& .MuiInputLabel-root.Mui-focused': {
                    color: '#000000',
                  },
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel
                  sx={{
                    '&.Mui-focused': {
                      color: '#000000',
                    },
                  }}
                >
                  Status
                </InputLabel>
                <Select
                  value={formData.status}
                  onChange={(e) => handleFormChange('status', e.target.value)}
                  label="Status"
                  sx={{
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#000000',
                    },
                  }}
                >
                  {statusOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      <Box display="flex" alignItems="center" gap={1}>
                        {option.icon}
                        {option.label}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 2 }}>
          <Button
            onClick={handleCloseDialog}
            sx={{ color: '#757575' }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            sx={{
              backgroundColor: '#000000',
              color: '#ffffff',
              fontWeight: 600,
              px: 3,
              '&:hover': {
                backgroundColor: '#333333',
              },
            }}
          >
            {editingSection ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        maxWidth="sm"
        PaperProps={{
          sx: {
            borderRadius: '12px',
          },
        }}
      >
        <DialogTitle>
          <Typography variant="h6" fontWeight={600}>
            Delete Section
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete "{sectionToDelete?.name}"? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button
            onClick={() => setDeleteDialogOpen(false)}
            sx={{ color: '#757575' }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            variant="contained"
            color="error"
            sx={{ fontWeight: 600 }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default ProductionPlantDetails; 